// Admin › Feedback: reports customers send from their Scout (Launcher › Send
// feedback), and the staff conversation about each one. Mirrors the
// /api/admin/feedback contract (vortex-api routes/adminFeedback).

export type FeedbackCategory = 'bug' | 'suggestion' | 'question' | 'other';
export type FeedbackSeverity = 'critical' | 'major' | 'minor';
export type FeedbackStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type FeedbackVisibility = 'internal' | 'customer';

export const FEEDBACK_CATEGORIES: readonly FeedbackCategory[] = ['bug', 'suggestion', 'question', 'other'];
export const FEEDBACK_STATUSES: readonly FeedbackStatus[] = ['open', 'in_progress', 'resolved', 'closed'];

export interface FeedbackPerson {
  id: string;
  name: string | null;
  email: string;
}

export interface FeedbackRow {
  id: string;
  /** The six digits everyone sees and reads out: "#307456". */
  reference: number;
  category: FeedbackCategory;
  severity: FeedbackSeverity | null;
  affected_app: string | null;
  description: string;
  status: FeedbackStatus;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  vessel_name: string | null;
  device: { id: string; serial_number: string } | null;
  account: FeedbackPerson | null;
  assignee: FeedbackPerson | null;
  /** Free-text owner from the old Neptune portal, before owners were users. */
  legacy_assigned_to: string | null;
  comment_count: number;
  customer_reply_count: number;
  /** Who wrote the latest comment the customer can see: them, Vortex, or nobody yet. */
  last_reply_from: 'customer' | 'vortex' | null;
  /** Closed, and the customer closed it from their Scout. */
  closed_by_customer: boolean;
  /** Waiting on Vortex (Needs a reply): the customer wrote last and did not then close it, or it is active with no reply yet. */
  waiting: boolean;
}

/** Why a customer closed their report on the Scout. */
export type FeedbackCloseReason = 'working_now' | 'my_mistake' | 'not_needed';

export interface FeedbackEvent {
  id: string;
  kind: 'comment' | 'status' | 'assignee';
  visibility: FeedbackVisibility;
  body: string | null;
  /** status/assignee events: { from, to }; assignee values are user ids. The customer closing it adds { reason }. */
  meta: { from?: string | null; to?: string | null; reason?: FeedbackCloseReason | null } | null;
  created_at: string;
  author: FeedbackPerson | null;
  /**
   * The customer did it on their Scout: a comment, or closing the report (a status
   * event whose body is their note). Status events with visibility customer are
   * Vortex resolving or reopening it, which the customer's Scout shows.
   */
  from_customer: boolean;
}

/**
 * What the Scout knew about itself when the report was sent, as the Launcher
 * builds it (LauncherViewModel feedback snapshot). Every key may be missing:
 * older reports carry `{}`.
 */
export interface FeedbackSystemInfo {
  device_id?: string;
  vessel_name?: string;
  timestamp?: string;
  timezone?: string;
  launcher_version?: string;
  app_versions?: Record<string, string> | Array<unknown>;
  android_version?: string;
  device_model?: string;
  screen?: string;
  battery?: { percent?: number; charging?: boolean; health?: string | number; temp_c?: number };
  wifi?: { ssid?: string; rssi?: number; ip?: string; connected?: boolean };
  gps?: { has_fix?: boolean; satellites_used?: number; accuracy_m?: number; lat?: number; lon?: number };
  cellular?: { type?: string; signal_level?: number; connected?: boolean };
  bluetooth?: { enabled?: boolean; paired_count?: number; connected_count?: number };
  nmea_connected?: boolean;
  la_status?: { connected?: boolean; registered?: boolean; tier?: string; days_remaining?: number };
  free_memory_mb?: number;
  storage_free_mb?: number;
  uptime_hours?: number;
  [key: string]: unknown;
}

export interface FeedbackDetail extends FeedbackRow {
  steps_to_reproduce: string | null;
  system_info: FeedbackSystemInfo | null;
  latitude: number | null;
  longitude: number | null;
  /** The old portal's single reply field, if this report predates the timeline. */
  notes: string | null;
  /** Oldest first. */
  events: FeedbackEvent[];
}

export interface FeedbackStaffMember {
  id: string;
  name: string | null;
  email: string;
  role: string;
}

export interface FeedbackListParams {
  status?: FeedbackStatus;
  category?: FeedbackCategory;
  /** 'me', 'none' or a user id. */
  assignee?: string;
  q?: string;
  /** Only reports waiting on Vortex: the customer wrote last (and did not then close it), or open or in progress with no reply yet. */
  waiting?: 1;
  /** 1-based. */
  page?: number;
  limit?: number;
}

/** How many reports each tab holds under the category, owner and search filters. */
export interface FeedbackCounts {
  waiting: number;
  open: number;
  in_progress: number;
  resolved: number;
  closed: number;
  all: number;
}

export interface FeedbackListPage {
  rows: FeedbackRow[];
  total: number;
  page: number;
  limit: number;
  counts: FeedbackCounts;
}

export interface FeedbackUpdateInput {
  status?: FeedbackStatus;
  assignee_user_id?: string | null;
  /** With a status change ("Mark resolved"): a reply the customer sees, saved in the same write. */
  reply?: string;
  /** With a status change: a note only staff see, saved in the same write. */
  note?: string;
}
